import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  Loader2,
  Mail,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import useReveal from "@/hooks/useReveal";
import { api } from "@/lib/api";
import { trackPageView, track } from "@/lib/analytics";

export default function PlanDetail() {
  const { slug } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Subscribe form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [formError, setFormError] = useState(null);

  const r = useReveal();

  useEffect(() => {
    trackPageView();
    setLoading(true);
    api
      .get(`/plans/${slug}`)
      .then(({ data }) => setPlan(data))
      .catch((err) => {
        setError(
          err?.response?.status === 404
            ? "Plano não encontrado."
            : "Não foi possível carregar este plano agora."
        );
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/plans/subscribe", {
        plan_slug: slug,
        name: name.trim(),
        email: email.trim(),
      });
      setSuccess({
        already: !!data.already_subscribed,
        currentDay: data.current_day,
      });
      track("plan_subscribe", {
        plan_slug: slug,
        already_subscribed: !!data.already_subscribed,
      });
      setName("");
      setEmail("");
    } catch (err) {
      setFormError(
        err?.response?.data?.detail ||
          "Não conseguimos inscrever você agora. Tente novamente em alguns instantes."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="plan-detail-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      <main className="pt-32 pb-24">
        <section className="max-w-6xl mx-auto px-6 md:px-8">
          <Link
            to="/planos"
            data-testid="plan-back-link"
            className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.24em] text-[#0B1E36]/60 hover:text-[#C5A059] transition-colors"
          >
            <ArrowLeft size={12} strokeWidth={1.7} />
            Todos os planos
          </Link>

          {loading ? (
            <div className="mt-12 h-[400px] bg-[#F5F7FA] animate-pulse" />
          ) : error ? (
            <p
              data-testid="plan-error"
              className="mt-16 text-[#6B7280]"
            >
              {error}
            </p>
          ) : plan ? (
            <>
              <div ref={r} className="reveal mt-10 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-7">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="h-px w-10 bg-[#C5A059]" aria-hidden />
                    <span className="text-[0.7rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                      Plano de Estudo · {plan.duration_days} dias
                    </span>
                  </div>
                  <h1
                    data-testid="plan-title"
                    className="font-serif-display text-[#0B1E36] text-4xl md:text-5xl lg:text-[3.6rem] leading-[1.05] tracking-[-0.028em] font-medium"
                  >
                    {plan.title}
                  </h1>
                  {plan.subtitle && (
                    <p className="mt-5 text-[#0B1E36]/70 italic font-serif-display text-[1.2rem] md:text-[1.4rem] leading-[1.4]">
                      {plan.subtitle}
                    </p>
                  )}
                  <p className="mt-8 text-[1.05rem] text-[#6B7280] leading-[1.85] font-light">
                    {plan.description}
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-[0.72rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
                    <span className="inline-flex items-center gap-2">
                      <CalendarDays size={13} strokeWidth={1.6} />
                      {plan.duration_days} dias
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <Mail size={13} strokeWidth={1.6} />
                      Conteúdo entregue por e-mail
                    </span>
                    {plan.subscriber_count > 0 && (
                      <span
                        data-testid="plan-subscriber-count"
                        className="text-[#C5A059]"
                      >
                        {plan.subscriber_count}{" "}
                        {plan.subscriber_count === 1
                          ? "pessoa inscrita"
                          : "pessoas inscritas"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Subscribe card */}
                <aside className="lg:col-span-5">
                  <div
                    data-testid="plan-subscribe-card"
                    className="bg-white border border-[#0B1E36]/10 p-8 lg:sticky lg:top-32"
                  >
                    {success ? (
                      <div data-testid="plan-subscribe-success">
                        <div className="flex items-center gap-3 text-[#C5A059]">
                          <CheckCircle2 size={20} strokeWidth={1.6} />
                          <span className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
                            Inscrição confirmada
                          </span>
                        </div>
                        <p className="mt-5 font-serif-display italic text-[#0B1E36] text-[1.4rem] leading-[1.4]">
                          {success.already
                            ? "Você já estava inscrito(a) nesse plano."
                            : "Pronto. O Dia 1 acabou de ser enviado para o seu e-mail."}
                        </p>
                        <p className="mt-5 text-[#6B7280] text-sm leading-[1.7] font-light">
                          {success.already
                            ? "Continue sua jornada por onde parou. Nenhum e-mail extra será enviado hoje."
                            : "Amanhã, no mesmo horário, chega o Dia 2. Você pode acompanhar o conteúdo abaixo enquanto isso."}
                        </p>
                        <button
                          onClick={() => setSuccess(null)}
                          data-testid="plan-subscribe-reset"
                          className="mt-6 text-[0.72rem] uppercase tracking-[0.24em] text-[#0B1E36]/60 hover:text-[#C5A059] underline-offset-4 hover:underline transition-colors"
                        >
                          Inscrever outra pessoa
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3 text-[#C5A059]">
                          <Mail size={16} strokeWidth={1.6} />
                          <span className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
                            Inscreva-se gratuitamente
                          </span>
                        </div>
                        <p className="mt-5 font-serif-display italic text-[#0B1E36] text-[1.45rem] leading-[1.35] tracking-[-0.01em]">
                          Receba 1 dia por e-mail. <br />
                          No seu ritmo. Sem ruído.
                        </p>
                        <form
                          onSubmit={submit}
                          data-testid="plan-subscribe-form"
                          className="mt-7 space-y-4"
                        >
                          <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Seu nome"
                            data-testid="plan-subscribe-name"
                            required
                            minLength={2}
                            className="w-full border border-[#0B1E36]/15 bg-white py-3 px-4 text-sm text-[#0B1E36] placeholder:text-[#0B1E36]/40 focus:outline-none focus:border-[#C5A059]"
                          />
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Seu melhor e-mail"
                            data-testid="plan-subscribe-email"
                            required
                            className="w-full border border-[#0B1E36]/15 bg-white py-3 px-4 text-sm text-[#0B1E36] placeholder:text-[#0B1E36]/40 focus:outline-none focus:border-[#C5A059]"
                          />
                          <button
                            type="submit"
                            disabled={submitting || !name.trim() || !email.trim()}
                            data-testid="plan-subscribe-submit"
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#0B1E36] text-white px-6 py-3 text-xs uppercase tracking-[0.22em] font-medium hover:bg-[#13294B] transition-colors disabled:opacity-60"
                          >
                            {submitting ? (
                              <>
                                <Loader2
                                  size={13}
                                  className="animate-spin"
                                />
                                Inscrevendo…
                              </>
                            ) : (
                              <>
                                Iniciar agora
                                <ArrowUpRight
                                  size={13}
                                  strokeWidth={1.7}
                                />
                              </>
                            )}
                          </button>
                          <p className="text-[0.7rem] text-[#0B1E36]/55 leading-[1.55]">
                            Você recebe o Dia 1 imediatamente após confirmar.
                            Pode cancelar quando quiser.
                          </p>
                          {formError && (
                            <div
                              data-testid="plan-subscribe-error"
                              className="text-xs px-3 py-2 border border-rose-300 bg-rose-50 text-rose-800"
                            >
                              {formError}
                            </div>
                          )}
                        </form>
                      </>
                    )}
                  </div>
                </aside>
              </div>

              {/* Days list */}
              <section
                data-testid="plan-days-list"
                className="mt-24 border-t border-[#0B1E36]/10 pt-16"
              >
                <h2 className="text-[0.7rem] uppercase tracking-[0.28em] text-[#C5A059] font-medium mb-10">
                  Os {plan.duration_days} dias da jornada
                </h2>
                <ul className="divide-y divide-[#0B1E36]/8">
                  {(plan.days || []).map((d) => (
                    <li
                      key={d.day_number}
                      data-testid={`plan-day-${d.day_number}`}
                      className="py-8 grid grid-cols-12 gap-6"
                    >
                      <div className="col-span-12 md:col-span-2">
                        <span className="font-serif-display text-[2.4rem] text-[#0B1E36]/15 leading-none tabular-nums">
                          {String(d.day_number).padStart(2, "0")}
                        </span>
                        <p className="mt-1 text-[0.65rem] uppercase tracking-[0.28em] text-[#0B1E36]/45">
                          Dia {d.day_number}
                        </p>
                      </div>
                      <div className="col-span-12 md:col-span-10">
                        <h3 className="font-serif-display text-[#0B1E36] text-[1.5rem] md:text-[1.7rem] leading-[1.25] tracking-[-0.01em]">
                          {d.title}
                        </h3>
                        {d.verse_reference && (
                          <p className="mt-3 text-[0.78rem] uppercase tracking-[0.22em] text-[#C5A059] font-medium">
                            {d.verse_reference}
                          </p>
                        )}
                        {d.verse_text && (
                          <blockquote className="mt-2 font-serif-display italic text-[#0B1E36]/75 text-[1.05rem] leading-[1.65]">
                            “{d.verse_text}”
                          </blockquote>
                        )}
                        {d.reflection && (
                          <p className="mt-5 text-[#6B7280] text-[0.98rem] leading-[1.85] font-light line-clamp-4">
                            {d.reflection}
                          </p>
                        )}
                        {d.application && (
                          <div className="mt-5 border-l-2 border-[#C5A059] pl-4">
                            <p className="text-[0.7rem] uppercase tracking-[0.24em] text-[#C5A059]/85 font-medium">
                              Aplicação
                            </p>
                            <p className="mt-1 text-[#0B1E36] text-[0.95rem] leading-[1.65]">
                              {d.application}
                            </p>
                          </div>
                        )}
                        {d.sermon_slug && (
                          <Link
                            to={`/pregacoes/${d.sermon_slug}`}
                            data-testid={`plan-day-${d.day_number}-sermon-link`}
                            className="mt-6 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.24em] text-[#0B1E36]/70 hover:text-[#C5A059] transition-colors border-b border-[#0B1E36]/10 hover:border-[#C5A059]/60 pb-1.5"
                          >
                            Ler o sermão completo
                            <ArrowUpRight
                              size={12}
                              strokeWidth={1.7}
                            />
                          </Link>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          ) : null}
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
